import { useEffect, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { saveSearchParams } from "../utils/search-params-persistence";

/**
 * Hook to automatically persist search params for the current route.
 *
 * This hook saves the current URL search params to sessionStorage whenever they change,
 * allowing breadcrumb navigation to restore the filter state.
 *
 * @param options - Configuration options
 * @param options.enabled - Whether to enable persistence (default: true)
 */
export const usePersistedSearchParams = (
  options: { enabled?: boolean } = {},
) => {
  const { enabled = true } = options;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    // Get the base path without query params
    const basePath = location.pathname;

    // Skip saving on first render if params are empty
    // This prevents clearing params when navigating to a page via breadcrumb
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Only save on first render if there are actual params
      if (searchParams.toString()) {
        saveSearchParams(basePath, searchParams);
      }
      return;
    }

    // Save params whenever they change
    saveSearchParams(basePath, searchParams);
  }, [searchParams, location.pathname, enabled]);
};

export default usePersistedSearchParams;
